import React, { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { rowsToCsv, downloadCsv } from "../../lib/csv";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

const cellValue = (row, col) => {
  const v = row[col.key];
  if (col.key === "created_at") return fmtDate(v);
  if (col.type === "consent") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join("; ");
  return v ?? "";
};

const ConsentBadge = ({ yes }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${yes ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{yes ? "Yes" : "No"}</span>
);

// Modal that lists captured leads (name/email/etc.) for an analytics box, with CSV download.
export default function LeadListModal({ open, onClose, title, columns, fetcher, filename }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setRows(null);
    fetcher()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line

  const download = () => {
    const csv = rowsToCsv(columns, rows || [], cellValue);
    downloadCsv(`${filename}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="lead-list-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 pr-8">
            <DialogTitle className="font-display">{title}</DialogTitle>
            <Button
              data-testid="lead-list-download"
              onClick={download}
              disabled={loading || !rows || rows.length === 0}
              className="bg-orange hover:bg-orange-dark text-white rounded-full h-9 shrink-0"
            >
              <Download size={15} className="mr-1.5" /> Download CSV
            </Button>
          </div>
          <DialogDescription className="sr-only">List of captured contacts with a CSV download option.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-slate-400 py-16 text-center flex items-center justify-center gap-2" data-testid="lead-list-loading">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </p>
        ) : !rows || rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-16 text-center" data-testid="lead-list-empty">Nobody captured here yet.</p>
        ) : (
          <div className="overflow-auto -mx-1 px-1">
            <p className="text-xs text-slate-400 mb-3" data-testid="lead-list-count">{rows.length} record{rows.length === 1 ? "" : "s"}</p>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  {columns.map((c) => (
                    <th key={c.key} className="py-2 pr-4 font-semibold text-slate-500 whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="lead-list-rows">
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                    {columns.map((c) => (
                      <td key={c.key} className="py-2 pr-4 text-navy align-top max-w-[280px] truncate" title={String(cellValue(r, c))}>
                        {c.type === "consent" ? <ConsentBadge yes={!!r[c.key]} /> : cellValue(r, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
